import type { ComponentType, ReactNode } from "react";
import { parse as parseYaml } from "yaml";
import { GithubPanel } from "./GithubPanel.js";
import { DatabaseCheckPanel } from "./DatabaseCheckPanel.js";
import { AppearancePanel } from "./AppearancePanel.js";

/** Props an `ExtraUI` panel receives — a handle on the raw-YAML box it sits
 * under, so a panel (e.g. the favicon uploader) can propose an edit into it. The
 * box still owns the bytes; Save/publish stays the one persistence path. Panels
 * that don't need these (GitHub, DB check) simply ignore them. */
export interface ConfigPanelProps {
  /** Current box content. */
  text: string;
  /** Propose replacing the box content (mirrors the textarea's onChange). */
  setText: (next: string) => void;
  /** The config repo (`datawiki-config`). */
  repo: string;
  /** This file's path (e.g. `appearance`). */
  path: string;
  /** Whether the viewer may edit here. */
  editable: boolean;
}

/**
 * Per-file metadata for a `datawiki-config` file, layered over the raw-YAML box
 * baseline (docs/dev/WikiConfig.md "Editing model"). Data-driven: a file type is
 * just an entry in CONFIG_FILE_TYPES keyed by its path, so adding richer editing
 * for a new config file is one registry entry, no changes to the editor.
 *
 *   instructions — help shown above the YAML box.
 *   validate     — client-side check run before save, *after* the YAML syntax
 *                  gate every config file gets (`validateConfig`); a returned
 *                  string blocks the save and is shown inline (null = ok). The
 *                  brutalist box still owns the bytes; this just catches the
 *                  obvious foot-guns before a publish hot-reloads bad config.
 *                  (Server-side pre-receive validation is the separate
 *                  hardening — docs/git/plans.md.)
 *   ExtraUI      — an extra panel below the box (e.g. the GitHub device flow),
 *                  handed a ConfigPanelProps handle on the box.
 */
export interface ConfigFileType {
  instructions?: ReactNode;
  validate?: (text: string) => string | null;
  ExtraUI?: ComponentType<ConfigPanelProps>;
}

/** Parse a config file's scalar keys, mirroring the server's `parseKV` — same
 * YAML library, same normalization to strings, so what validates here is what
 * the server will read. Only scalars: a list value is left out, and every
 * `validate` below is about scalars. Assumes valid YAML (`yamlError` runs
 * first and blocks the save). */
function parseKV(text: string): Map<string, string> {
  const out = new Map<string, string>();
  let doc: unknown;
  try {
    doc = parseYaml(text);
  } catch {
    return out;
  }
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return out;
  for (const [key, value] of Object.entries(doc as Record<string, unknown>)) {
    if (value !== null && typeof value !== "object") out.set(key, String(value));
  }
  return out;
}

/**
 * The YAML syntax error in `text`, or null when it parses. Runs for **every**
 * `datawiki-config` file, not just the ones with a registered type: these files
 * are YAML, the server reads them with a YAML parser, and a file that doesn't
 * parse says nothing at all once published. Catching it at the keystroke is the
 * cheap place — the alternative is a silent no-op setting.
 */
export function yamlError(text: string): string | null {
  try {
    parseYaml(text);
    return null;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // The library's message carries a line/col and a source excerpt; keep the
    // first line, which is the part that says what's wrong and where.
    return `not valid YAML — ${message.split("\n")[0]}`;
  }
}

/**
 * Everything that blocks Save for a config file: the YAML syntax gate, then the
 * per-file validator when the path has one. Null = fine.
 */
export function validateConfig(path: string, text: string): string | null {
  return yamlError(text) ?? configTypeFor(path)?.validate?.(text) ?? null;
}

const CONFIG_FILE_TYPES: Record<string, ConfigFileType> = {
  appearance: {
    instructions: (
      <p className="dw-muted">
        <code>wordmark</code> is the header text. <code>icon</code> names a favicon
        image committed to <code>datawiki-config</code> — set it below rather than
        by hand.
      </p>
    ),
    validate: (text) => {
      const kv = parseKV(text);
      const known = new Set(["wordmark", "icon"]);
      for (const key of kv.keys()) {
        if (!known.has(key)) return `unknown key "${key}" — appearance takes wordmark and icon`;
      }
      return null;
    },
    ExtraUI: AppearancePanel,
  },
  github: {
    instructions: (
      <>
        <p className="dw-muted">
          <code>clientId</code> is the GitHub App's device-flow client ID. It's a
          public identifier, not a secret. With it set, connect one shared push
          account below — the device-flow token is stored encrypted; until then
          edits commit locally only.
        </p>
      </>
    ),
    validate: (text) => {
      const kv = parseKV(text);
      const known = new Set(["clientId"]);
      for (const key of kv.keys()) {
        if (!known.has(key)) return `unknown key "${key}" — github takes only clientId`;
      }
      // clientId may be blank (local-only); only flag a malformed file above.
      return null;
    },
    ExtraUI: GithubPanel,
  },
};

/** Config files whose path is a *shape*, not a constant — one entry per entity
 * in a collection directory. Checked after the exact-path table above. */
const CONFIG_FILE_PATTERNS: [RegExp, ConfigFileType][] = [
  // databases/<name>/connection — one external database per directory. The
  // directory name is the id a query names with `-- @source: <name>`.
  [/^databases\/[^/]+\/connection$/, {
    instructions: (
      <p className="dw-muted">
        One external database. This directory's name is its id — the{" "}
        <code>-- @source: &lt;name&gt;</code> a query selects it with.{" "}
        <code>url</code> is a secret <em>reference</em>, not a value:{" "}
        <code>env:DATABASE_URL</code> or <code>env-file:/path</code> — the value
        stays out of git. <code>params</code> is a list of{" "}
        <code>key=value</code> connection options merged into that url's query
        string (<code>sslmode=verify-full</code>,{" "}
        <code>sslrootcert=/path/ca.pem</code>, <code>application_name=…</code>) —
        for when the url comes fixed from the host.{" "}
        <code>statementTimeoutMs</code>, <code>maxRows</code> and{" "}
        <code>maxConcurrent</code> (queries at once — the pool holds that many
        connections plus 2 for checks and health pings) tune it,{" "}
        <code>label</code> names it in the data card, and{" "}
        <code>default: true</code> makes it where a query with no{" "}
        <code>@source</code> runs. Publish to apply — the connection re-opens
        without a restart; check it live below.
      </p>
    ),
    validate: (text) => {
      const kv = parseKV(text);
      const known = new Set([
        "url",
        "params",
        "statementTimeoutMs",
        "maxRows",
        "maxConcurrent",
        "label",
        "default",
      ]);
      for (const key of kv.keys()) {
        if (!known.has(key)) return `unknown key "${key}"`;
      }
      const url = kv.get("url");
      if (url && !/^(env|env-file):/.test(url)) {
        return "url must be a reference — env:NAME or env-file:PATH (no plaintext secrets)";
      }
      // Only the one-line form is visible here — parseKV keeps scalars, and the
      // bullet list under a bare `params:` is checked server-side at connect.
      const params = kv.get("params");
      if (params && !params.includes("=")) {
        return "params entries are key=value, e.g. sslmode=require";
      }
      for (const n of ["statementTimeoutMs", "maxRows", "maxConcurrent"]) {
        const v = kv.get(n);
        if (v && !Number.isFinite(Number(v))) return `${n} must be a number`;
      }
      const conc = kv.get("maxConcurrent");
      if (conc && Number(conc) < 1) {
        return "maxConcurrent must be at least 1 — 0 would gate every query forever";
      }
      const dflt = kv.get("default");
      if (dflt && dflt !== "true" && dflt !== "false") {
        return 'default is true or false — "true" makes this the database a query with no @source runs on';
      }
      return null;
    },
    ExtraUI: DatabaseCheckPanel,
  }],
];

/** The descriptor for a config file path (e.g. "github", or any
 * `databases/<name>/connection`), or undefined for the plain raw-YAML
 * baseline. Exact paths win over patterns. */
export function configTypeFor(path: string): ConfigFileType | undefined {
  const exact = CONFIG_FILE_TYPES[path];
  if (exact) return exact;
  return CONFIG_FILE_PATTERNS.find(([re]) => re.test(path))?.[1];
}
